import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/store/auth';

interface CargaItem {
  id: number;
  tipo: string;
  filename: string;
  uploaded_at: string | null;
  uploaded_by_id: number | null;
  parse_status: string;
  candidatos_afectados: number;
}

export function PlantillaListaTab({ listaId }: { listaId: number }) {
  const { rol } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [resultado, setResultado] = useState<any | null>(null);

  const puedeSubir = ['PLT', 'PLA', 'ADM', 'ASI', 'CAR_DJHV'].includes(rol ?? '');

  const { data, isLoading } = useQuery<{ total: number; items: CargaItem[] }>({
    queryKey: ['plantilla-cargas', listaId],
    queryFn: async () =>
      (await api.get('/plantillas', { params: { lista_id: listaId, limit: 20 } })).data,
  });

  const descargarBlank = async () => {
    const r = await api.get(`/plantillas/lista/${listaId}/blank.xlsx`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `lista_${listaId}_plantilla.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/plantillas/lista/${listaId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (d) => {
      setResultado(d);
      qc.invalidateQueries({ queryKey: ['plantilla-cargas', listaId] });
      qc.invalidateQueries({ queryKey: ['lista', listaId] });
    },
  });

  return (
    <div className="bg-white border rounded p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-arpp-primary flex items-center gap-2">
            <FileSpreadsheet size={18} /> Plantilla consolidada (XLSX)
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Descarga el XLSX pre-llenado con los candidatos actuales de esta lista. Complétalo con los
            datos que faltan (sexo, fecha de nacimiento, dirección, etc.) y vuelve a subirlo. El sistema
            aplica los cambios sobre los candidatos existentes — <strong>no</strong> crea ni elimina filas.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={descargarBlank}
          className="bg-arpp-primary hover:bg-arpp-primary/90 text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2"
        >
          <Download size={16} /> Descargar plantilla (XLSX)
        </button>

        {puedeSubir && (
          <>
            <input
              type="file"
              accept=".xlsx"
              ref={fileRef}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setResultado(null);
                  uploadMut.mutate(f);
                }
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadMut.isPending}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Upload size={16} /> {uploadMut.isPending ? 'Subiendo…' : 'Subir XLSX completado'}
            </button>
          </>
        )}
      </div>

      {resultado && (
        <div
          className={`border rounded p-3 text-sm ${
            resultado.parse_status === 'parsed'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
              : 'bg-amber-50 border-amber-300 text-amber-900'
          }`}
        >
          <div className="flex items-center gap-2 font-medium">
            {resultado.parse_status === 'parsed' ? (
              <CheckCircle2 size={16} />
            ) : (
              <AlertTriangle size={16} />
            )}
            Resultado: {resultado.parse_status} · {resultado.candidatos_afectados} candidatos
            actualizados
          </div>
          {resultado.campos_aplicados && Object.keys(resultado.campos_aplicados).length > 0 && (
            <div className="text-xs mt-1">
              Campos aplicados:{' '}
              {Object.entries(resultado.campos_aplicados)
                .map(([k, v]: any) => `${k}(${Array.isArray(v) ? v.length : 1})`)
                .join(' · ')}
            </div>
          )}
          {resultado.parse_errors?.items?.length > 0 && (
            <ul className="text-xs mt-2 list-disc list-inside">
              {resultado.parse_errors.items.slice(0, 5).map((er: any, i: number) => (
                <li key={i}>
                  Fila {er.fila} · orden {er.orden}: {er.error}
                </li>
              ))}
              {resultado.parse_errors.items.length > 5 && (
                <li>…y {resultado.parse_errors.items.length - 5} errores más</li>
              )}
            </ul>
          )}
        </div>
      )}

      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Historial de cargas</div>
        {isLoading ? (
          <div className="text-sm text-gray-500">Cargando…</div>
        ) : !data?.items.length ? (
          <div className="text-sm text-gray-500 italic">Sin cargas previas.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-2 py-1">Fecha</th>
                <th className="text-left px-2 py-1">Archivo</th>
                <th className="text-left px-2 py-1">Estado</th>
                <th className="text-right px-2 py-1">Afectados</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="px-2 py-1">
                    {c.uploaded_at ? new Date(c.uploaded_at).toLocaleString('es-PE') : '—'}
                  </td>
                  <td className="px-2 py-1 font-mono">{c.filename}</td>
                  <td className="px-2 py-1">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] ${
                        c.parse_status === 'parsed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : c.parse_status === 'error'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {c.parse_status}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">{c.candidatos_afectados}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
